import { CARD_FIELD_OPTIONS } from "../types/v2/Shop";

/**
 * Fields a toast can display. A superset of the card-layout catalog the shop
 * already configures, minus "Custom" (which resolves against per-shop card
 * config and has no meaning in a toast), plus the fields a toast needs.
 */
export const TOAST_FIELD_KEYS = [
  ...CARD_FIELD_OPTIONS.filter((k) => k !== "Custom"),
  "Promise Time",
  "Container",
  "Technician",
  "Service Advisor",
] as const;

export type ToastFieldKey = (typeof TOAST_FIELD_KEYS)[number];

/** Number of field slots on a toast. */
export const TOAST_FIELD_SLOTS = 3;

/** How long after firing a client may still surface the toast. */
export const TOAST_ELIGIBILITY_WINDOW_MS = 5 * 60 * 1000;

/**
 * Which kind of automation raised a toast, so the client can apply the right
 * sound switch. "promiseTime" is any automation with an `onPromiseTime`-typed
 * trigger; everything else is "automation".
 */
export type AutomationToastCategory = "automation" | "promiseTime";

/**
 * Reads a stored payload category. Toasts written before categories existed
 * (or by a not-yet-redeployed function), and any unknown value, are plain
 * automation toasts.
 * @param {unknown} raw - The stored `ToastNotification.category`.
 * @returns {AutomationToastCategory} A known category, defaulting to "automation".
 */
export function normalizeToastCategory(raw: unknown): AutomationToastCategory {
  return raw === "promiseTime" ? "promiseTime" : "automation";
}

export type ShowToastActionValue = {
  headline: string;
  /** Always exactly TOAST_FIELD_SLOTS long; null means an empty slot. */
  fields: (ToastFieldKey | null)[];
};

export const DEFAULT_SHOW_TOAST_ACTION_VALUE: ShowToastActionValue = {
  headline: "",
  fields: [null, null, null],
};

/**
 * @param {unknown} v - Candidate field key.
 * @returns {boolean} True when v is a known toast field key.
 */
function isToastFieldKey(v: unknown): v is ToastFieldKey {
  return (TOAST_FIELD_KEYS as readonly string[]).includes(v as string);
}

/**
 * Pads or truncates to exactly TOAST_FIELD_SLOTS, nulling unknown keys.
 * @param {unknown} raw - Candidate fields array.
 * @returns {(ToastFieldKey | null)[]} Normalized slots.
 */
function normalizeFields(raw: unknown): (ToastFieldKey | null)[] {
  const input = Array.isArray(raw) ? raw : [];
  return Array.from({ length: TOAST_FIELD_SLOTS }, (_, i) =>
    isToastFieldKey(input[i]) ? (input[i] as ToastFieldKey) : null
  );
}

/**
 * @param {ShowToastActionValue} value - The value to serialize.
 * @returns {string} JSON for storage in Action.actionValue.
 */
export function encodeShowToastActionValue(
  value: ShowToastActionValue
): string {
  return JSON.stringify(value);
}

/**
 * Parses Action.actionValue, always returning a usable value.
 * @param {string | number | null | undefined} raw - The stored action value.
 * @returns {ShowToastActionValue} The parsed value, or the default.
 */
export function parseShowToastActionValue(
  raw: string | number | null | undefined
): ShowToastActionValue {
  if (typeof raw !== "string" || raw.trim() === "") {
    return {
      ...DEFAULT_SHOW_TOAST_ACTION_VALUE,
      fields: [...DEFAULT_SHOW_TOAST_ACTION_VALUE.fields],
    };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ShowToastActionValue>;
    return {
      headline: typeof parsed.headline === "string" ? parsed.headline : "",
      fields: normalizeFields(parsed.fields),
    };
  } catch {
    return {
      ...DEFAULT_SHOW_TOAST_ACTION_VALUE,
      fields: [...DEFAULT_SHOW_TOAST_ACTION_VALUE.fields],
    };
  }
}

/**
 * Builds the card payload for a fired toast.
 * @param {ShowToastActionValue} value - The action's configuration.
 * @param {Date} now - The application timestamp (passed in so tests can pin it).
 * @param {AutomationToastCategory} [category] - Which sound switch governs this toast; defaults to "automation".
 * @returns {object} A single toast payload; the caller appends it onto the
 *   card's `toastNotifications` array.
 */
export function buildToastNotification(
  value: ShowToastActionValue,
  now: Date,
  category: AutomationToastCategory = "automation"
): {
  headline: string;
  fields: (ToastFieldKey | null)[];
  firedAt: Date;
  expiresAt: Date;
  category: AutomationToastCategory;
} {
  return {
    headline: value.headline,
    fields: [...value.fields],
    firedAt: now,
    expiresAt: new Date(now.getTime() + TOAST_ELIGIBILITY_WINDOW_MS),
    category,
  };
}
